import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowLeft, FiArrowRight, FiClock, FiTarget, FiTrendingUp, FiUsers, FiStar, FiX, FiSearch } = FiIcons;

const ResultsPage = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [isSticky, setIsSticky] = useState(false);
  const [currentGallery, setCurrentGallery] = useState('');

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  // Exercise carousel images
  const exerciseImages = [
    'https://i.ibb.co/prnVXWry/Results.png',
    'https://i.ibb.co/kgZcPMWH/Results-3.png',
    'https://i.ibb.co/DD4MGQst/Results-2.png',
    'https://i.ibb.co/x8GvC19j/Results-1.png',
    'https://i.ibb.co/JFF9VMMc/Results-4.png',
    'https://i.ibb.co/2YWbsHPj/Results-5.png',
    'https://i.ibb.co/tMjkWhLN/Results-7.png',
    'https://i.ibb.co/TBCH4h43/Results-8.png'
  ];

  // Diet gallery images
  const dietImages = [
    'https://i.ibb.co/6cDxNxjM/Food-Advice-7.png',
    'https://i.ibb.co/1HqGSRj/Food-Advice-6.png',
    'https://i.ibb.co/Q7KGps5r/Food-Advice-5.png',
    'https://i.ibb.co/nqPZvhNf/Food-Advice-4.png',
    'https://i.ibb.co/B5r2gX8Y/Food-Advice-3.png',
    'https://i.ibb.co/vvVDW4y9/Food-Advice-2.png',
    'https://i.ibb.co/rDWy29B/Food-Advice.png',
    'https://i.ibb.co/yF0C3GvC/Food-Advice-8.png'
  ];

  // Testimonial images
  const testimonialImages = [
    'https://i.ibb.co/xKV9x8Fz/Snehal-Toshniwal-Testimonial-2.png',
    'https://i.ibb.co/vWGjR6H/Dhiraj-Gandhi-Testimonial.png',
    'https://i.ibb.co/ks7F4Kxk/Chirag-Kaswa-Testimonial.png',
    'https://i.ibb.co/HTcPxj1S/Jidnya-Shetiya-Testimonial.png'
  ];

  // Trusted by logos
  const trustedByLogos = [
    { src: 'https://i.ibb.co/N8NGbgw/pudhari-logo.png', alt: 'Pudhari logo' },
    { src: 'https://i.ibb.co/3nv0BWX/Screenshot-15-auto-x2-300x165.jpg', alt: 'WEAN logo' },
    { src: 'https://i.ibb.co/5xF01ffM/Screenshot-13-auto-x2-300x255.jpg', alt: 'Mrs India Fitness logo' },
    { src: 'https://i.ibb.co/Hcc88pz/kolhapur-sports-clup-428x443-1-290x300.png', alt: 'Kolhapur Sports Club logo' }
  ];

  // Case studies
  const caseStudies = [
    {
      name: "Jidnya Mehta",
      title: "CEO, Innovative Solutions",
      quote: "As someone who leads a team of 50+ professionals, I can't afford health setbacks. Sam's Nutri Hub has been my secret weapon. Their approach isn't just about diet—it's about optimizing performance through precision nutrition. Since working with them, my energy levels have doubled, and I've never been sharper in the boardroom."
    },
    {
      name: "Vikram Sharma",
      title: "Tech Entrepreneur",
      quote: "After three months with Sam's Nutri Hub, I've experienced a complete transformation in my energy levels and mental clarity. Their personalized approach to nutrition has helped me maintain peak performance during 14-hour startup days. What impressed me most was how they adapted my nutrition plan to accommodate my frequent travel and irregular schedule."
    },
    {
      name: "Dr. Priya Desai",
      title: "Cardiologist",
      quote: "As a physician, I was skeptical of nutrition programs, but Sam's approach is evidence-based and practical. After 10 weeks, I've lost 8kg while managing my hospital shifts. Their protocol has helped regulate my hormones and significantly reduced my PCOD symptoms. The nutrition strategies they've taught me have become a sustainable part of my lifestyle."
    },
    {
      name: "Rajesh Kapoor",
      title: "Investment Banker",
      quote: "The last three months with Sam's Nutri Hub have revolutionized my approach to health. Their data-driven nutrition protocol resolved my chronic digestive issues that no other specialist could fix. I've optimized my sleep quality and cognitive function, which has directly improved my decision-making in high-pressure financial situations. The ROI on this investment has been extraordinary."
    }
  ];

  // Quote testimonials
  const quoteTestimonials = [
    {
      name: "Rahul P.",
      result: "Lost 18kg in 6 months",
      quote: "I was skeptical at first, but after our initial consultation, I knew Sam's team was different. They actually understood my lifestyle and built a nutrition plan that made sense for me."
    },
    {
      name: "Anjali P.",
      result: "Reversed pre-diabetes",
      quote: "The team at Sam's Nutri Hub fixed my relationship with food in weeks, not months. My energy levels increased by 50% in the first month alone."
    },
    {
      name: "Vikram K.",
      result: "Gained 12kg muscle mass",
      quote: "What impressed me most was how they thought about the long-term. They didn't just fix my immediate weight issues—they built habits that have lasted for years."
    }
  ];

  // Speed cards
  const speedCards = [
    {
      title: "Days 7–10",
      description: "Noticeable energy and mental clarity."
    },
    {
      title: "Weeks 3–4",
      description: "Visible physical changes and better appetite control."
    },
    {
      title: "90 Days",
      description: "Body composition, sleep quality, and performance stabilization."
    }
  ];

  // Filter diet images
  const filteredDietImages = activeFilter === 'all' 
    ? dietImages 
    : dietImages.filter((_, index) => index % 5 === (activeFilter === 'performance' ? 0 : activeFilter === 'medical' ? 1 : activeFilter === 'fat-loss' ? 2 : 3));

  // Handle exercise carousel navigation
  const nextExercise = () => {
    setCurrentExerciseIndex((prev) => (prev + 1) % exerciseImages.length);
  };

  const prevExercise = () => {
    setCurrentExerciseIndex((prev) => (prev - 1 + exerciseImages.length) % exerciseImages.length);
  };

  // Handle lightbox
  const openLightbox = (index, gallery) => {
    setLightboxIndex(index);
    setCurrentGallery(gallery);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextLightboxImage = () => {
    const images = currentGallery === 'exercise' ? exerciseImages : 
                   currentGallery === 'diet' ? filteredDietImages : 
                   currentGallery === 'testimonial' ? testimonialImages : [];
    setLightboxIndex((prev) => (prev + 1) % images.length);
  };

  const prevLightboxImage = () => {
    const images = currentGallery === 'exercise' ? exerciseImages : 
                   currentGallery === 'diet' ? filteredDietImages : 
                   currentGallery === 'testimonial' ? testimonialImages : [];
    setLightboxIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      
      if (e.key === 'Escape') {
        closeLightbox();
      } else if (e.key === 'ArrowRight') {
        nextLightboxImage();
      } else if (e.key === 'ArrowLeft') {
        prevLightboxImage();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, currentGallery, filteredDietImages]);

  // Sticky filter bar
  useEffect(() => {
    const handleScroll = () => {
      const section6 = document.getElementById('diet-gallery');
      if (section6) {
        const rect = section6.getBoundingClientRect();
        setIsSticky(rect.top <= 0 && rect.bottom >= 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#F9FFF5] to-[#EAF8EC] py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeInUp}>
            <h1 className="text-4xl md:text-5xl font-bold text-[#0B1320] font-poppins mb-6">
              Real Results. Real Lives.
            </h1>
            <p className="text-xl text-[#4A4A4A] mb-4 max-w-2xl mx-auto">
              What happens when precision nutrition meets real-world execution.
            </p>
            <p className="text-[#9E9E9E] mb-10">
              1000+ lives transformed · Results visible in weeks · Built for high-performers
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/body-analysis"
                className="bg-[#2E7D32] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#205C25] transition-all duration-200 transform hover:translate-y-[-1px] hover:shadow-lg text-center"
              >
                Start Your Free Assessment
              </Link>
              <Link
                to="/plans"
                className="text-[#2E7D32] bg-transparent px-8 py-4 rounded-lg font-semibold hover:bg-[#2E7D32] hover:text-white transition-all duration-200 text-center border-2 border-[#2E7D32]"
              >
                See Plans & Pricing
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Outcome Summary */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B1320] font-poppins">
              What You'll Get When You Invest in Us
            </h2>
          </motion.div>
          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {[
              "Sustainable weight change without extreme measures",
              "Energy & focus lift within 7–10 days",
              "Personalized plans aligned to your lifestyle",
              "Simple movement guidance that fits your schedule",
              "Stress, sleep, and recovery dialled in",
              "Weekly refinements based on real progress"
            ].map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-[#2E7D32]"
              >
                <p className="text-[#4A4A4A]">{item}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Trusted By */}
      <section className="bg-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-8">
            <p className="text-[#9E9E9E] text-sm">Trusted by elite institutions</p>
          </motion.div>
          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center"
          >
            {trustedByLogos.map((logo, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="flex items-center justify-center grayscale hover:grayscale-0 transition-all duration-300"
              >
                <img
                  src={logo.src}
                  alt={logo.alt}
                  className="max-h-12 w-auto object-contain"
                />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="bg-[#FFFDF8] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B1320] font-poppins mb-4">
              The Transformation Chronicles
            </h2>
            <p className="text-xl text-[#4A4A4A] max-w-3xl mx-auto">
              Real results from high-performing professionals who demanded more.
            </p>
          </motion.div>
          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-8"
          >
            {caseStudies.map((study, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-white p-8 rounded-xl shadow-lg"
              >
                <div className="flex items-center mb-6">
                  <div className="bg-[#2E7D32] w-12 h-12 rounded-full flex items-center justify-center text-white font-bold mr-4">
                    {study.name.charAt(0)}
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0B1320]">{study.name}</h3>
                    <p className="text-sm text-[#2E7D32] font-medium">{study.title}</p>
                  </div>
                </div>
                <p className="text-[#4A4A4A] italic">"{study.quote}"</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Visual Results - Exercise Carousel */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <div className="inline-block mb-4">
              <span className="bg-[#2E7D32] text-white px-4 py-2 rounded-full text-sm font-medium">
                Workout ✤ Diet ✤ Health ✤ Wellness ✤
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B1320] font-poppins mb-4">
              From Instructions to Triumph — Exercise Results
            </h2>
            <p className="text-xl text-[#4A4A4A] max-w-3xl mx-auto">
              Swipe through real client journeys—from precise exercise inputs to measurable outcomes. Every slide captures commitment, progression, and results.
            </p>
          </motion.div>
          {/* Carousel */}
          <motion.div {...fadeInUp} className="relative max-w-4xl mx-auto">
            <div 
              className="overflow-hidden rounded-2xl shadow-xl cursor-pointer"
              onClick={() => openLightbox(currentExerciseIndex, 'exercise')}
            >
              <img
                src={exerciseImages[currentExerciseIndex]}
                alt="Exercise inputs and real client results"
                className="w-full h-96 object-cover"
              />
            </div>
            {/* Navigation Arrows */}
            <button
              onClick={prevExercise}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-3 shadow-lg transition-all duration-200"
              aria-label="Previous slide"
            >
              <SafeIcon icon={FiArrowLeft} className="h-6 w-6 text-[#2E7D32]" />
            </button>
            <button
              onClick={nextExercise}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-3 shadow-lg transition-all duration-200"
              aria-label="Next slide"
            >
              <SafeIcon icon={FiArrowRight} className="h-6 w-6 text-[#2E7D32]" />
            </button>
            {/* Dots Indicator */}
            <div className="flex justify-center mt-6 space-x-2">
              {exerciseImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentExerciseIndex(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-200 ${
                    index === currentExerciseIndex ? 'bg-[#2E7D32] w-6' : 'bg-gray-300'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </motion.div>
          <motion.div {...fadeInUp} className="text-center mt-12">
            <Link
              to="/body-analysis"
              className="inline-block border-2 border-[#2E7D32] text-[#2E7D32] bg-white px-8 py-4 rounded-lg font-semibold hover:bg-[#2E7D32] hover:text-white transition-all duration-200"
            >
              Find out what your body needs
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Visual Results - Personalized Diet Gallery */}
      <section id="diet-gallery" className="bg-[#FFFDF8] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B1320] font-poppins mb-4">
              Personalized Diet Plans — Built Around Real Lives
            </h2>
            <p className="text-xl text-[#4A4A4A] max-w-3xl mx-auto">
              Local, affordable, and tailored. Explore the actual plan snapshots clients used to transform.
            </p>
          </motion.div>
          {/* Sticky Filter Bar */}
          <div className={`sticky top-16 z-40 mb-12 transition-all duration-300 ${isSticky ? 'bg-white/90 backdrop-blur-sm py-4 shadow-md' : 'py-0'}`}>
            <div className="flex flex-wrap justify-center gap-2">
              {['all', 'performance', 'medical', 'fat-loss', 'weight-gain'].map((filter) => (
                <button
                  key={filter}
                  onClick={() => setActiveFilter(filter)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    activeFilter === filter
                      ? 'bg-[#2E7D32] text-white'
                      : 'bg-gray-100 text-[#4A4A4A] hover:bg-gray-200'
                  }`}
                >
                  {filter.charAt(0).toUpperCase() + filter.slice(1)}
                </button>
              ))}
            </div>
          </div>
          {/* Gallery Grid */}
          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
          >
            {filteredDietImages.map((image, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="relative overflow-hidden rounded-xl shadow-lg cursor-pointer group"
                onClick={() => openLightbox(index, 'diet')}
              >
                <img
                  src={image}
                  alt="Personalized diet plan snapshots from WhatsApp"
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <SafeIcon icon={FiSearch} className="h-8 w-8 text-white" />
                </div>
              </motion.div>
            ))}
          </motion.div>
          <motion.div {...fadeInUp} className="text-center mt-12">
            <Link
              to="/plans"
              className="inline-block border-2 border-[#2E7D32] text-[#2E7D32] bg-white px-8 py-4 rounded-lg font-semibold hover:bg-[#2E7D32] hover:text-white transition-all duration-200"
            >
              See Plan Options
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B1320] font-poppins">
              What Our Clients Say
            </h2>
          </motion.div>
          {/* Image Wall */}
          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16"
          >
            {testimonialImages.map((image, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="overflow-hidden rounded-xl shadow-lg cursor-pointer group"
                onClick={() => openLightbox(index, 'testimonial')}
              >
                <img
                  src={image}
                  alt="Client transformation testimonial"
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <SafeIcon icon={FiSearch} className="h-8 w-8 text-white" />
                </div>
              </motion.div>
            ))}
          </motion.div>
          {/* Quote Cards */}
          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {quoteTestimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-[#F9FFF5] p-8 rounded-xl"
              >
                <div className="flex items-center mb-6">
                  <div className="bg-[#2E7D32] w-12 h-12 rounded-full flex items-center justify-center text-white font-bold mr-4">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0B1320]">{testimonial.name}</h3>
                    <p className="text-sm text-[#2E7D32] font-medium">{testimonial.result}</p>
                  </div>
                </div>
                <p className="text-[#4A4A4A] italic">"{testimonial.quote}"</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Speed of Results */}
      <section className="bg-[#EAF8EC] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B1320] font-poppins mb-4">
              Speed of Results
            </h2>
            <p className="text-xl text-[#4A4A4A] max-w-3xl mx-auto">
              When you follow our protocols, here's what typically happens—and when.
            </p>
          </motion.div>
          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {speedCards.map((card, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-white p-8 rounded-xl shadow-lg text-center"
              >
                <h3 className="text-xl font-semibold text-[#0B1320] mb-4 font-poppins">{card.title}</h3>
                <p className="text-[#4A4A4A]">{card.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#205C25] py-20 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp}>
            <h3 className="text-3xl md:text-4xl font-bold text-white font-poppins mb-6">
              Ready to See Real Results?
            </h3>
            <p className="text-xl text-gray-200 mb-8 max-w-3xl mx-auto">
              Join 1000+ professionals who've transformed their health with our precision nutrition approach.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/body-analysis"
                className="bg-white text-[#205C25] px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-200 transform hover:translate-y-[-1px] hover:shadow-lg text-center"
              >
                Start Your Free Assessment
              </Link>
              <Link
                to="/plans"
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-[#205C25] transition-all duration-200 transform hover:translate-y-[-1px] hover:shadow-lg text-center"
              >
                Explore Our Portfolios
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white p-2 rounded-full hover:bg-white/10 transition-colors"
            aria-label="Close lightbox"
          >
            <SafeIcon icon={FiX} className="h-8 w-8" />
          </button>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              prevLightboxImage();
            }}
            className="absolute left-4 text-white p-3 rounded-full hover:bg-white/10 transition-colors"
            aria-label="Previous image"
          >
            <SafeIcon icon={FiArrowLeft} className="h-8 w-8" />
          </button>
          
          <div 
            className="max-w-4xl max-h-[90vh] cursor-pointer"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={
                currentGallery === 'exercise' ? exerciseImages[lightboxIndex] :
                currentGallery === 'diet' ? filteredDietImages[lightboxIndex] :
                currentGallery === 'testimonial' ? testimonialImages[lightboxIndex] : ''
              }
              alt="Full size view"
              className="max-h-[80vh] object-contain"
            />
          </div>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              nextLightboxImage();
            }}
            className="absolute right-4 text-white p-3 rounded-full hover:bg-white/10 transition-colors"
            aria-label="Next image"
          >
            <SafeIcon icon={FiArrowRight} className="h-8 w-8" />
          </button>
        </div>
      )}
    </div>
  );
};

export default ResultsPage;